import React from 'react';
import {
  AddEmployeesIcon,
  Box,
  Button,
  ButtonMode,
  LoadingSpinner,
  SetBenefitsIcon,
  Notification,
  NotificationType,
  NotificationSize,
  ButtonSize,
  PlusIcon,
  UploadIcon
} from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import config from '@epi-config';
import { goToPage } from '@epi-actions/navigation';
import { EmptyState, Pagination } from '@epi-shared/components';
import { routerPaths } from '@epi-constants/routerPaths';
import { BeneficiaryItemsPerPage } from '@epi-constants/beneficiary';
import { selectImportStatus } from '@epi-store/beneficiariesImport/beneficiariesImport.selectors';

import { useBeneficiariesList } from './hooks';
import BeneficiariesModal from './components/BeneficiariesModal/BeneficiariesModal';
import { BeneficiariesTable } from './components/BeneficiariesTable';
import { BeneficiariesTopSection } from './components/BeneficiariesTopSection';
import { BeneficiariesFilters } from './components/BeneficiariesFilters/BeneficiariesFilters';
import { AddBeneficiaries } from './components/AddBeneficiaries';

const { enableMassUpload } = config;

const BeneficiariesList = () => {
  const { fontBold, fontSemiBold, primaryFrontColor } = useTheme();
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const importStatus = useSelector(selectImportStatus);
  const {
    isGetEmployeesLoading,
    isGroupSettingsLoading,
    isOnboardingLoading,
    groupSettings,
    employees,
    filteredEmployees,
    selectedBeneficiaryId,
    showBeneficiaryAddedNotification,
    handleEmployeesChange,
    handleGroupChange,
    handleEmploymentTypeChange,
    showBeneficiaryModal,
    handleClose,
    setIsEmployeeAdded,
    setPage,
    page,
    paginationCount,
    sortedEmployees,
    showDetail,
    employeesOptions,
    benefitGroupOptions,
    employmentTypeOptions,
    selectedBenefitGroup,
    selectedEmploymentType,
    setshowBeneficiaryModal,
    showNotification,
    handleCloseNotification,
    preselectedGroupName
  } = useBeneficiariesList();

  const handleAddEmployees = () => {
    setshowBeneficiaryModal(true);
  };
  const handleImportEmployees = () => {
    reduxDispatch(goToPage(routerPaths.importEmployees));
  };

  const renderPagePanel = () => {
    if (
      isGetEmployeesLoading ||
      isGroupSettingsLoading ||
      isOnboardingLoading
    ) {
      return <LoadingSpinner />;
    }

    if (!groupSettings?.length) {
      return (
        <EmptyState
          icon={<SetBenefitsIcon />}
          title={t('employees.set_benefits_title')}
          description={t('employees.set_benefits_description')}
          btnText={t('employees.set_benefits_btn')}
          btnOnClick={() => reduxDispatch(goToPage(routerPaths.manageBenefits))}
        />
      );
    }

    if (!employees?.beneficiaries.length) {
      return (
        <AddBeneficiaries
          handleAddEmployees={handleAddEmployees}
          handleImportEmployees={handleImportEmployees}
        />
      );
    }

    return (
      <div>
        {showBeneficiaryAddedNotification && <BeneficiariesTopSection />}
        <BeneficiariesFilters
          employeesOptions={employeesOptions}
          selectedBenefitGroup={selectedBenefitGroup}
          benefitGroupOptions={benefitGroupOptions}
          selectedEmploymentType={selectedEmploymentType}
          employmentTypeOptions={employmentTypeOptions}
          handleEmployeesChange={handleEmployeesChange}
          handleGroupChange={handleGroupChange}
          handleEmploymentTypeChange={handleEmploymentTypeChange}
        />
        {showNotification && (
          <Box pt={3}>
            <Notification
              type={NotificationType.Info}
              size={NotificationSize.Small}
              text={
                <Trans
                  i18nKey="employees.filter.group_preselected"
                  components={{
                    b: <Box fontWeight={fontBold} display="inline-block" />
                  }}
                  values={{
                    groupName: preselectedGroupName
                  }}
                />
              }
              onClose={handleCloseNotification}
            />
          </Box>
        )}
        <BeneficiariesTable
          beneficiaries={sortedEmployees}
          allBeneficiaries={filteredEmployees}
          isLoading={isGetEmployeesLoading}
          showDetail={showDetail}
          groupSettings={groupSettings}
        />
        {filteredEmployees.length > BeneficiaryItemsPerPage && (
          <Pagination
            page={page}
            count={paginationCount}
            defaultRowsPerPage={BeneficiaryItemsPerPage}
            onChangePage={page => setPage(page)}
          />
        )}
      </div>
    );
  };

  return (
    <Box>
      {!!employees?.beneficiaries?.length && (
        <Box display="flex" gap={3} py={3}>
          {enableMassUpload && (
            <Button
              mode={ButtonMode.Secondary}
              size={ButtonSize.Medium}
              onClick={() => {
                reduxDispatch(goToPage(routerPaths.importEmployees));
              }}
              id="import-employees-btn"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <UploadIcon color={primaryFrontColor} />
                {t('containers.beneficiaries.import_employees.title')}
              </Box>
            </Button>
          )}
          {!!groupSettings?.length && (
            <Button
              mode={ButtonMode.Secondary}
              size={ButtonSize.Medium}
              onClick={() => {
                setshowBeneficiaryModal(true);
              }}
              id="add-employee-btn"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <PlusIcon color={primaryFrontColor} />
                {t('containers.beneficiaries.add_employee.trigger')}
              </Box>
            </Button>
          )}
        </Box>
      )}

      {enableMassUpload && importStatus?.status === 'Processed' && (
        <Box pb={4}>
          <Box pb={2} fontWeight={fontSemiBold}>
            <Notification
              type={NotificationType.Success}
              text={t(
                'containers.beneficiaries.import_employees.notifications.import_in_progress',
                {
                  toEdit: importStatus?.beneficiariesToUpdate,
                  toAdd: importStatus?.beneficiariesToCreate
                }
              )}
            />
          </Box>
          <Notification
            type={NotificationType.Important}
            text={t(
              'containers.beneficiaries.import_employees.notifications.cards_in_progress'
            )}
          />
        </Box>
      )}

      <Box minHeight={570}>{renderPagePanel()}</Box>

      <BeneficiariesModal
        isShowModal={showBeneficiaryModal}
        beneficiaryId={selectedBeneficiaryId}
        onClose={handleClose}
        setIsEmployeeAdded={setIsEmployeeAdded}
      />
    </Box>
  );
};

export default BeneficiariesList;
